import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import {
  fetchGpsRoute,
  fetchGpsSysadmin,
  gpsOfficesQuery,
  gpsPageQuery,
  gpsSysadminsQuery,
  type GpsOffice,
  type GpsSysadmin,
} from '../api/easeedeskGps'
import { Forbidden, isForbidden } from '../ui/Forbidden'
import { Spinner } from '../ui/Spinner'
import { loadGoogleMaps, type GMap, type GMapInfoWindow, type GMapMarker, type GoogleMaps } from './googleMaps'
import { assetUrl } from '../ui/assetUrl'
import './GpsTrackingPage.css'

/**
 * "Location Monitoring" - `Easeedesk::gps_tracking`, macheta
 * `views/version-3/location_monitoring/map.php`.
 *
 * Aceeasi harta: pastilele sediilor colorate dupa managerul de cont, pozele
 * oamenilor de teren, aceleasi ferestre la clic, aceleasi butoane de filtrare
 * (Admins / Customers, CheckIn / Commuting / Active / Inactive, Reset) si
 * acelasi drum al unui om intre doua momente.
 *
 * CHEIA GOOGLE MAPS nu mai sta in macheta, deci nu mai sta in git: vine de la
 * server, din `GOOGLE_MAPS_KEY`. Cand nu e pusa, ecranul SPUNE ca lipseste -
 * nu ramane o pagina alba pe care nimeni n-o poate explica.
 *
 * DRUMUL SE DESENEAZA ALTFEL, si e singura schimbare care se vede. In portal,
 * punctele inregistrate se trimiteau la `DirectionsService` ca origine,
 * destinatie si opt opriri, iar Google desena un traseu pe SOSELE intre ele -
 * adica un drum ghicit, nu cel facut. Aici se uneste chiar sirul de puncte
 * inregistrate. Se pastreaza in schimb ce spunea panoul din dreapta: viteza
 * maxima si cea medie, in km/h, socotite la fel.
 */

/** Cele patru butoane de filtrare a oamenilor, ca in macheta. */
const FILTERS: { key: string; label: string; icon: string; style?: string }[] = [
  { key: 'checkin', label: 'CheckIn', icon: 'fa fa-male' },
  { key: 'commuting', label: 'Commuting', icon: 'fa fa-bicycle' },
  { key: 'active', label: 'Active', icon: 'fa fa-male' },
  { key: 'inactive', label: 'Inactive', icon: 'fa fa-male', style: 'color: red' },
]

const ACTIVITY_TEXT: Record<string, { text: string; color: string }> = {
  commuting: { text: 'Commuting to:', color: '#ff6600' },
  checkedIn: { text: 'Checked in:', color: '#006600' },
  checkedOut: { text: 'Checked out:', color: '#c00a18' },
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

/** `round($v, 2)` din portal, pe viteza trecuta din m/s in km/h. */
function kmh(metresPerSecond: number): number {
  return Math.round(metresPerSecond * 3.6 * 100) / 100
}

export function GpsTrackingPage() {
  const page = useQuery(gpsPageQuery)
  const [show, setShow] = useState('')
  const [filter, setFilter] = useState('')
  const [admin, setAdmin] = useState(0)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [route, setRoute] = useState<{ max: number; avg: number; points: number } | null>(null)
  const [mapError, setMapError] = useState('')

  const sysadmins = useQuery({ ...gpsSysadminsQuery(filter), enabled: page.data?.ready === true && show !== 'offices' })
  const offices = useQuery({ ...gpsOfficesQuery, enabled: page.data?.ready === true && show !== 'sysadmins' })

  const box = useRef<HTMLDivElement>(null)
  const mapsRef = useRef<GoogleMaps | null>(null)
  const mapRef = useRef<GMap | null>(null)
  const markersRef = useRef<GMapMarker[]>([])
  const windowRef = useRef<GMapInfoWindow | null>(null)
  const lineRef = useRef<{ setMap(map: unknown): void } | null>(null)

  /* Harta se face o data, cand cheia si centrul sunt stiute. */
  useEffect(() => {
    const data = page.data
    if (!data || !data.ready || !box.current || mapRef.current) return

    /* Cheia lipsa se vede la desenare (`keyMissing`), nu se pune in stare:
       o stare scrisa dintr-un efect porneste inca o randare degeaba. */
    if (data.mapsKey === '') return

    let gone = false
    void loadGoogleMaps(data.mapsKey).then(
      (maps) => {
        if (gone || !box.current) return
        mapsRef.current = maps
        mapRef.current = new maps.Map(box.current, {
          zoom: 13,
          center: data.center,
          streetViewControl: true,
        })
        windowRef.current = new maps.InfoWindow({ content: '' })
      },
      (e: Error) => {
        if (!gone) setMapError(e.message)
      },
    )

    return () => {
      gone = true
    }
  }, [page.data])

  /* Markerele se redeseneaza la fiecare schimbare de filtru sau de date. */
  useEffect(() => {
    const maps = mapsRef.current
    const map = mapRef.current
    if (!maps || !map) return

    for (const marker of markersRef.current) marker.setMap(null)
    markersRef.current = []

    const drawn: GMapMarker[] = []

    if (show !== 'offices') {
      for (const person of sysadmins.data?.sysadmins ?? []) {
        drawn.push(personMarker(maps, map, person, windowRef.current))
      }
    }
    if (show !== 'sysadmins') {
      for (const office of offices.data?.offices ?? []) {
        drawn.push(officeMarker(maps, map, office, windowRef.current))
      }
    }

    markersRef.current = drawn
  }, [show, sysadmins.data, offices.data])

  const people = sysadmins.data?.sysadmins ?? []
  const keyMissing = page.data?.ready === true && page.data.mapsKey === ''
  const problem = keyMissing
    ? 'The map cannot be drawn: no Google Maps key is configured for this installation.'
    : mapError

  const showRoute = async () => {
    if (admin < 1 || from === '' || to === '') return
    const maps = mapsRef.current
    const map = mapRef.current
    if (!maps || !map) return

    const answer = await fetchGpsRoute(admin, from, to)
    lineRef.current?.setMap(null)
    lineRef.current = null

    if (answer.history.length === 0) {
      setRoute({ max: 0, avg: 0, points: 0 })
      return
    }

    let max = 0
    let sum = 0
    let counted = 0
    for (const point of answer.history) {
      if (point.speed > max) max = point.speed
      if (point.speed > 0) {
        sum += point.speed
        counted += 1
      }
    }

    lineRef.current = new maps.Polyline({
      path: answer.history.map((p) => ({ lat: p.latitude, lng: p.longitude })),
      geodesic: true,
      strokeColor: '#4e7db5',
      strokeOpacity: 0.9,
      strokeWeight: 4,
      map,
    })
    map.panTo({ lat: answer.history[0]!.latitude, lng: answer.history[0]!.longitude })

    setRoute({ max: kmh(max), avg: counted === 0 ? 0 : kmh(sum / counted), points: answer.history.length })
  }

  const reset = () => {
    setShow('')
    setFilter('')
    setAdmin(0)
    setFrom('')
    setTo('')
    setRoute(null)
    lineRef.current?.setMap(null)
    lineRef.current = null
  }

  if (isForbidden(page.error)) return <Forbidden />
  if (page.isPending) return <Spinner />

  if (page.data?.ready === false) {
    return (
      <div className="alert alert-warning" style={{ margin: 20 }}>
        <i className="fas fa-exclamation-triangle"></i> Location monitoring is not available on this installation.
      </div>
    )
  }

  return (
    <div className="gps-screen">
      <div className="gps-tabs panel-group" id="gpsTrackingTabs">
        <div className="panel panel-default gps-panel">
          <div className="panel-heading gps-panel-heading">
            <div className="gps-image-tabs">
              <i className="fa fa-map-marker"></i>
            </div>
            <div className="gps-description-tabs">IT Managers</div>
          </div>
          <div className="gps-panel-content">
            <table className="table">
              <tbody>
                <tr>
                  <td>
                    <img src={assetUrl("images/location_monitoring/red_dot.png")} alt="" style={{ width: 26, height: 26 }} />
                  </td>
                  <td></td>
                  <td>Without IT Manager</td>
                </tr>
                {(page.data?.accountManagers ?? [])
                  .filter((m) => m.companies !== 0)
                  .map((m) => (
                    <tr key={m.id}>
                      <td>
                        <img src={assetUrl(`images/location_monitoring/${m.color}.png`)} alt="" style={{ width: 26, height: 26 }} />
                      </td>
                      <td>{m.companies}</td>
                      <td>{m.name}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="panel panel-default gps-panel">
          <div className="panel-heading gps-panel-heading">
            <div className="gps-image-tabs">
              <i className="fas fa-search" style={{ fontSize: 12 }}></i>
            </div>
            <div className="gps-description-tabs">Filters</div>
          </div>
          <div className="gps-panel-content">
            <label htmlFor="gps-from">View History</label>
            <input
              id="gps-from"
              type="text"
              className="form-control"
              placeholder="Start date (YYYY/MM/DD HH:MM)"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
            />
            <input
              id="gps-to"
              type="text"
              className="form-control"
              style={{ marginTop: '2%' }}
              placeholder="End date (YYYY/MM/DD HH:MM)"
              value={to}
              onChange={(e) => setTo(e.target.value)}
            />

            <br />

            <label htmlFor="admin-select">Select administrator</label>
            <br />
            <div className="admins">
              <select id="admin-select" value={admin} onChange={(e) => setAdmin(Number(e.target.value))}>
                <option value={0}>Select administrator...</option>
                {people.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <label>View on map</label>
            <br />
            <button type="button" className="showRoute" onClick={() => void showRoute()}>
              <i className="fa fa-paper-plane"></i> <span>Route</span>
            </button>
            <button
              type="button"
              className={`showSysadmins${show === 'sysadmins' ? ' selected' : ''}`}
              onClick={() => setShow('sysadmins')}
            >
              <i className="fa fa-male"></i> <span>Admins</span>
            </button>
            <button
              type="button"
              className={`showCustomers${show === 'offices' ? ' selected' : ''}`}
              onClick={() => setShow('offices')}
            >
              <i className="fa fa-building"></i> <span>Customers</span>
            </button>

            {FILTERS.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`filter${filter === f.key ? ' selected' : ''}`}
                onClick={() => setFilter(f.key)}
              >
                <i className={f.icon} style={f.style === undefined ? undefined : { color: 'red' }}></i> <span>{f.label}</span>
              </button>
            ))}

            <button type="button" className="resetFilters" onClick={reset}>
              Reset filters
            </button>
          </div>
        </div>

        <div className="panel panel-default gps-panel" id="showAdminRoute">
          <div className="panel-heading gps-panel-heading">
            <div className="gps-image-tabs">
              <i className="fa fa-paper-plane" style={{ fontSize: 12 }}></i>
            </div>
            <div className="gps-description-tabs">Route details</div>
          </div>
          <div className="gps-panel-content">
            <div id="directions-panel">
              {route === null || route.points === 0 ? (
                <i>No route available.</i>
              ) : (
                <>
                  <i>MAX Speed:</i> <span>{route.max} km/h</span>
                  <br />
                  <i>AVG Speed:</i> <span>{route.avg} km/h</span>
                  <br />
                  <i>Points:</i> <span>{route.points}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {problem !== '' ? (
        <div className="alert alert-warning gps-map-missing">
          <i className="fas fa-exclamation-triangle"></i> {problem}
        </div>
      ) : null}
      <div id="map" ref={box}></div>
    </div>
  )
}

function personMarker(maps: GoogleMaps, map: GMap, person: GpsSysadmin, info: GMapInfoWindow | null): GMapMarker {
  const picture = person.picture === '' ? 'default_picture.jpg' : person.picture
  const marker = new maps.Marker({
    flat: true,
    icon: {
      url: `/application/views/hr/files/poza/${picture}?sysadmin=1`,
      anchor: new maps.Point(8, 8),
      scaledSize: new maps.Size(50, 50),
    },
    map,
    position: { lat: person.lat, lng: person.lng },
    visible: true,
    title: person.name,
  })

  marker.addListener('click', () => {
    void fetchGpsSysadmin(person.id, person.activity).then((details) => {
      if (!info) return
      const tone = ACTIVITY_TEXT[details.activity] ?? ACTIVITY_TEXT.commuting!
      info.setContent(
        `Name: ${escapeHtml(details.name)}<br/>` +
          `<b><span style="color:${tone.color}">${tone.text}</span> ${escapeHtml(details.company)} - ${escapeHtml(details.office)}</b><br/>` +
          `Fixed Location: ${details.fixedLocation ? 'Yes' : 'No'}<br/>` +
          `Last seen: ${escapeHtml(details.lastSeen)}<br/>` +
          `Car: ${escapeHtml(details.car)}<br/>` +
          `Latitude: ${details.lat}<br/>` +
          `Longitude: ${details.lng}`,
      )
      info.open(map, marker)
    })
  })

  return marker
}

function officeMarker(maps: GoogleMaps, map: GMap, office: GpsOffice, info: GMapInfoWindow | null): GMapMarker {
  const marker = new maps.Marker({
    flat: true,
    icon: {
      url: assetUrl(`images/location_monitoring/${office.color === '' ? 'red_dot' : office.color}.png`),
      anchor: new maps.Point(0, 0),
      scaledSize: new maps.Size(16, 16),
    },
    map,
    position: { lat: office.lat, lng: office.lng },
    visible: true,
    title: `${office.company} - ${office.office}`,
    zIndex: 99999,
  })

  marker.addListener('click', () => {
    if (!info) return
    info.setContent(
      `Name: ${escapeHtml(office.company)} - ${escapeHtml(office.office)}<br/>` +
        `Account Manager: ${escapeHtml(office.accountManager)}<br/>` +
        `Tickets: ${office.tickets}<br/>` +
        `Admin: ${escapeHtml(office.admin)}`,
    )
    info.open(map, marker)
  })

  return marker
}
